import { getAuthToken, AUTHORIZATION_HEADER_NAME } from '../utils/auth';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function getHeaders(authenticated: boolean): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (authenticated) {
    const token = getAuthToken();
    if (token) headers[AUTHORIZATION_HEADER_NAME as string] = `Token ${token}`;
  }
  return headers;
}

/** Build URL: base + path. User routes at /user/*, agent/chyps at /api/agent/* and /api/chyps/*. */
function buildApiUrl(path: string): string {
  const base = BASE_URL.replace(/\/$/, '');
  const normalizedPath = path.replace(/^\//, '');
  return `${base}/${normalizedPath}`;
}

async function callApi(path: string, method: string, body?: object, authenticated = false): Promise<any> {
  const url = buildApiUrl(path);
  const res = await fetch(url, {
    method,
    headers: getHeaders(authenticated),
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.message || data.detail || 'Request failed'), { response: res, data });
  return data;
}

export function login(
  email: string,
  password: string,
  termsConsent?: {
    terms_version: string;
    consent_timestamp: number;
    browser_user_agent?: string;
    browser_language?: string;
    browser_platform?: string;
    consent_data?: Record<string, unknown>;
  }
): Promise<any> {
  const body: Record<string, unknown> = { email, password };
  if (termsConsent?.consent_timestamp) {
    body.terms_version = termsConsent.terms_version || '1.0';
    body.consent_timestamp = termsConsent.consent_timestamp;
    if (termsConsent.browser_user_agent != null) body.browser_user_agent = termsConsent.browser_user_agent;
    if (termsConsent.browser_language != null) body.browser_language = termsConsent.browser_language;
    if (termsConsent.browser_platform != null) body.browser_platform = termsConsent.browser_platform;
    if (termsConsent.consent_data) body.consent_data = termsConsent.consent_data;
  }
  return callApi('user/login', 'POST', body);
}

export function logout(): Promise<any> {
  return callApi('user/logout', 'POST', undefined, true);
}

/** Sign up with consent. Requires group_name, terms_version, consent_timestamp. Returns success and message; if email_verification_required, user must click link in email to activate. */
export function signUp(payload: {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  group_name: string;
  tier_id?: number;
  terms_version: string;
  consent_timestamp: number;
  browser_user_agent?: string;
  browser_language?: string;
  browser_platform?: string;
  consent_data?: Record<string, unknown>;
}): Promise<{ success: boolean; message?: string; data?: { email?: string; email_verification_required?: boolean; auth_token?: string } }> {
  return callApi('user/sign-up', 'POST', payload);
}

/** Verify signup email via token from link. No auth. */
export function verifyEmail(token: string): Promise<{ success: boolean; message?: string; data?: { email?: string } }> {
  return callApi(`user/verify-email?token=${encodeURIComponent(token)}`, 'GET');
}

/** Check if signup email has been verified (user active, no pending token). No auth. Poll after signup to redirect to login. */
export function checkSignupVerified(email: string): Promise<{ success: boolean; verified: boolean; email?: string }> {
  return callApi(`user/check-verified?email=${encodeURIComponent(email)}`, 'GET');
}

/** Submit Enterprise tier contact form. Sends email to admin (ADMIN_USER_EMAIL). No auth. */
export function enterpriseInquiry(payload: {
  email: string;
  phone_number?: string;
  name?: string;
  company?: string;
}): Promise<{ success: boolean; message?: string }> {
  return callApi('user/enterprise-inquiry', 'POST', payload);
}

export function getTiers(): Promise<{ success: boolean; data: Array<{ id: number; name: string; description: string }> }> {
  return callApi('user/tiers', 'GET');
}

/** Current user's tier, quota, usage, and role. Auth required. */
export function getTiersMe(): Promise<{
  success: boolean;
  data: {
    tier: string;
    allowed_services: string[];
    usage_this_month?: number;
    monthly_quota?: number;
    max_questions?: number;
    total_questions_used?: number;
    max_sub_accounts?: number;
    sub_account_count?: number;
    is_group_admin?: boolean;
    user_group_id?: number | null;
    user_role?: 'admin' | 'sub_account';
    over_quota_payment_url?: string | null;
  };
}> {
  return callApi('user/tiers/me', 'GET', undefined, true);
}

/** List sub-accounts for the current user (group admin). Auth required. */
export function getSubAccounts(): Promise<{
  success: boolean;
  data: { sub_accounts: Array<{ id: number; username: string; first_name: string; last_name: string; created_at: string | null }> };
}> {
  return callApi('user/sub-accounts', 'GET', undefined, true);
}

/** Create a sub-account (username, first_name, last_name, password). No email. Auth required (group admin, within quota). */
export function createSubAccount(payload: {
  username: string;
  first_name: string;
  last_name: string;
  password: string;
}): Promise<{
  success: boolean;
  data?: { user: { id: number; username: string; first_name: string; last_name: string } };
}> {
  return callApi('user/sub-accounts', 'POST', payload, true);
}

/** Remove a sub-account by user id. Auth required (group admin); only your own sub-accounts can be removed. */
export function removeSubAccount(userId: number): Promise<{ success: boolean; message?: string }> {
  return callApi('user/sub-accounts/remove', 'POST', { user_id: userId }, true);
}

export function mfaSetup(): Promise<{ success: boolean; data: { secret: string; qr_code: string; uri: string } }> {
  return callApi('user/mfa/setup', 'POST', {}, true);
}

/** Extract backup_codes from verify-setup response (handles various shapes). */
function extractBackupCodes(res: any): string[] {
  if (!res || typeof res !== 'object') return [];
  const data = res.data;
  const raw =
    (data && (data as Record<string, unknown>).backup_codes) ??
    (data && (data as Record<string, unknown>).backupCodes) ??
    (res as Record<string, unknown>).backup_codes ??
    (res as Record<string, unknown>).backupCodes;
  if (Array.isArray(raw)) {
    const codes = raw.map((c: unknown) => (typeof c === 'string' ? c : String(c ?? '')));
    if (codes.length > 0) return codes;
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const codes = Object.values(raw).map((c: unknown) => (typeof c === 'string' ? c : String(c ?? '')));
    if (codes.length > 0) return codes;
  }
  // Recursive fallback: find first array that looks like backup codes (e.g. proxy-wrapped response)
  const findCodeArray = (obj: unknown): string[] => {
    if (Array.isArray(obj)) {
      const mapped = obj.map((c: unknown) => (typeof c === 'string' ? c : String(c ?? '')));
      if (mapped.length > 0 && mapped.every((s) => s.length >= 6 && s.length <= 12)) return mapped;
      return [];
    }
    if (obj && typeof obj === 'object')
      for (const v of Object.values(obj)) {
        const found = findCodeArray(v);
        if (found.length > 0) return found;
      }
    return [];
  };
  return findCodeArray(res.data ?? res);
}

export function mfaVerifySetup(code: string): Promise<{
  success: boolean;
  message?: string;
  backupCodes: string[];
}> {
  return callApi('user/mfa/verify-setup', 'POST', { code }, true).then((res: any) => ({
    success: Boolean(res?.success),
    message: res?.message,
    backupCodes: extractBackupCodes(res),
  }));
}

export function mfaVerifyLogin(email: string, code: string): Promise<any> {
  return callApi('user/mfa/verify-login', 'POST', { email, code });
}

export function getProfile(): Promise<any> {
  return callApi('user/admin/me', 'GET', undefined, true);
}

export function adminHealth(): Promise<any> {
  return callApi('user/admin/health', 'GET', undefined, true);
}

export function adminUsersList(): Promise<any> {
  return callApi('user/admin/users-list', 'GET', undefined, true);
}

/** Admin-only: delete a user by id. Returns { success, message }. Cannot delete self. */
export function adminDeleteUser(userId: number): Promise<{ success: boolean; message?: string }> {
  return callApi('user/admin/user-delete', 'POST', { user_id: userId }, true);
}

/** Admin-only: reset any non-admin user's password. */
export function adminResetPassword(userId: number, newPassword: string): Promise<{ success: boolean; message?: string }> {
  return callApi('user/admin/reset-password', 'POST', { user_id: userId, new_password: newPassword }, true);
}

/** Send password reset email. Always returns success to prevent enumeration. */
export function requestPasswordReset(email: string): Promise<{ success: boolean; message?: string }> {
  return callApi('user/request-password-reset', 'POST', { email });
}

/** Confirm password reset with token from email link. */
export function confirmPasswordReset(token: string, newPassword: string): Promise<{ success: boolean; message?: string }> {
  return callApi('user/confirm-password-reset', 'POST', { token, new_password: newPassword });
}

/** Ping backend with browser timestamp for connectivity / health logging. No auth. */
export function pingBackend(): Promise<{ ok: boolean; message: string }> {
  return callApi('health/ping', 'POST', {
    client_timestamp: new Date().toISOString(),
  });
}

/** Record a site visit (drschmoe.ai). No auth. Backend aggregates by geohash. */
export function recordVisit(): Promise<{ success: boolean }> {
  return callApi('user/visit', 'POST', {}, false);
}

/** Admin: fetch user signup statistics by tier (signups, active, groups). */
export function adminUserStats(): Promise<any> {
  return callApi('user/admin/user-stats', 'GET', undefined, true);
}

/** Admin: fetch lead library and conversion statistics. */
export function adminLeadStats(): Promise<any> {
  return callApi('user/admin/lead-stats', 'GET', undefined, true);
}

/** Admin: fetch visit map data (lat, lon, count) for world heat map. */
export function adminVisitMap(): Promise<{ success: boolean; data: { regions: Array<{ lat: number; lon: number; count: number }> } }> {
  return callApi('user/admin/visit-map', 'GET', undefined, true);
}

export function adminVideoPipeline(annotationsJson: object, demoName: string): Promise<{ success: boolean; job_id?: string; message?: string }> {
  return callApi('user/admin/video-pipeline', 'POST', { annotations_json: annotationsJson, demo_name: demoName }, true);
}

export function adminVideoPipelineStatus(jobId: string): Promise<{ success: boolean; status: string; logs: string; result?: { output_video: string; size_mb: number }; error?: string }> {
  return callApi('user/admin/video-pipeline/status', 'POST', { job_id: jobId }, true);
}

/** Current Terms of Service version (e.g. "1.2"). No auth. */
export function getTermsVersion(): Promise<{ success: boolean; data: { terms_version: string } }> {
  return callApi('user/terms-version', 'GET', undefined, false);
}

/** Raw markdown content for a given ToS version (reads from .md file on backend). No auth. */
export function getTermsContent(version: string): Promise<{ success: boolean; data: { content: string } }> {
  const path = `user/terms-content?version=${encodeURIComponent(version)}`;
  return callApi(path, 'GET', undefined, false);
}

/** Record that the authenticated user agreed to this ToS version at this time. Stored in RDS. */
export function termsAgree(termsVersion: string): Promise<{ success: boolean; data: { terms_version: string; agreed_at: string } }> {
  return callApi('user/terms-agree', 'POST', { terms_version: termsVersion }, true);
}

/** Guest ask: optional auth. If authenticated (any tier), no turn limit. If guest, 2 turns per 2 hours per IP.
 * Send auth when user is logged in so backend grants unlimited turns. Optional conversation_history for multi-turn.
 * 429 is handled without throwing so the console does not show an error (normal guest limit). */
export async function agentGuestAsk(
  question: string,
  conversationHistory?: Array<{ role: string; content: string }>
): Promise<{
  answer: string;
  sources: Array<{ id: number; source_file: string; chunk_index: number }>;
  guest_remaining: number | null;
  guest_limit?: boolean;
  message?: string;
}> {
  const body: { question: string; conversation_history?: Array<{ role: string; content: string }> } = { question };
  if (conversationHistory?.length) body.conversation_history = conversationHistory;
  const url = buildApiUrl('api/agent/guest-ask/');
  const res = await fetch(url, {
    method: 'POST',
    headers: getHeaders(true),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 429) {
    return {
      answer: '',
      sources: [],
      guest_remaining: 0,
      guest_limit: true,
      message: data.message || 'You’ve reached the guest limit (2 questions per 2 hours). Please register or log in for more.',
    };
  }
  if (!res.ok) throw Object.assign(new Error(data.message || data.detail || 'Request failed'), { response: res, data });
  return data;
}

/** Agent: ask a question (RAG over Dr. Schmoe notes). Auth required. */
export function agentAsk(question: string, topK = 10): Promise<{
  answer: string;
  sources: Array<{ id: number; source_file: string; chunk_index: number }>;
  input_tokens: number;
  output_tokens: number;
}> {
  return callApi('api/agent/ask/', 'POST', { question, top_k: topK }, true);
}

/** Agent: get personalized rehab plan from patient context. Auth required. */
export function agentRehabPlan(params: {
  patient_summary?: string;
  age?: number | null;
  sport?: string;
  symptoms?: string;
  issues?: string;
  preliminary_diagnosis?: string;
  extra_notes?: string;
  top_k?: number;
}): Promise<{
  plan: string;
  sources: Array<{ id: number; source_file: string; chunk_index: number }>;
  input_tokens: number;
  output_tokens: number;
}> {
  return callApi('api/agent/rehab-plan/', 'POST', params, true);
}

// ---------- CHYPS Intake (patient link + clinician) ----------

/** Resolve token; no auth. Returns { valid, already_submitted, form, title, error? }. Does not throw on 404/410 — returns valid: false instead. */
export async function chypsByToken(token: string): Promise<{
  valid: boolean;
  already_submitted?: boolean;
  form?: string;
  title?: string;
  error?: string;
}> {
  const url = buildApiUrl(`api/chyps/by-token/${encodeURIComponent(token)}/`);
  const res = await fetch(url, { method: 'GET', headers: getHeaders(false) });
  const data = await res.json().catch(() => ({}));
  if (res.ok) return data;
  if (res.status === 404 || res.status === 410) {
    return { valid: false, error: data.error || 'This link is invalid or has expired. It may have been removed by your clinician.' };
  }
  throw Object.assign(new Error(data.error || data.detail || data.message || 'Could not load form.'), { response: res, data });
}

/** Submit questionnaire answers by token; no auth. */
export function chypsSubmitByToken(token: string, answers: Record<string, unknown>): Promise<{ success: boolean; message?: string }> {
  return callApi(`api/chyps/by-token/${encodeURIComponent(token)}/submit/`, 'POST', { answers }, false);
}

/** Create or reuse link for UPID; auth required. Returns { url, token, upid, created }. */
export function chypsCreateLink(upid: string): Promise<{ url: string; token: string; upid: string; created: boolean }> {
  return callApi('api/chyps/links/', 'POST', { upid: upid.trim() }, true);
}

/** List links for current user; auth required. */
export function chypsListLinks(): Promise<{
  links: Array<{
    id: number;
    token: string;
    upid: string;
    created_at: string;
    status: 'pending' | 'submitted';
    submitted_at: string | null;
  }>;
}> {
  return callApi('api/chyps/links/list/', 'GET', undefined, true);
}

/** Delete a link by id; auth required. Only the creator can delete. */
export async function chypsDeleteLink(linkId: number): Promise<void> {
  const url = buildApiUrl(`api/chyps/links/${linkId}/`);
  const res = await fetch(url, { method: 'DELETE', headers: getHeaders(true) });
  if (res.status === 204) return;
  const data = await res.json().catch(() => ({}));
  throw Object.assign(new Error(data.error || data.detail || 'Failed to delete link'), { response: res, data });
}

/** List responses for current user; auth required. */
export function chypsListResponses(): Promise<{
  responses: Array<{
    id: number;
    upid: string;
    submitted_at: string;
    scores: Record<string, unknown>;
    link_id: number;
  }>;
}> {
  return callApi('api/chyps/responses/', 'GET', undefined, true);
}

/** Fetch PDF (or HTML fallback) for a response; auth required. Triggers browser download. */
export async function chypsDownloadResponsePdf(responseId: number): Promise<void> {
  const url = buildApiUrl(`api/chyps/responses/${responseId}/pdf/`);
  const res = await fetch(url, { headers: getHeaders(true) });
  if (!res.ok) throw new Error('Failed to download PDF');
  const contentType = res.headers.get('Content-Type') || '';
  const disposition = res.headers.get('Content-Disposition');
  const suggested = disposition?.match(/filename="?([^";]+)"?/)?.[1];
  const filename =
    suggested ||
    (contentType.includes('text/html') ? `chyps_response_${responseId}.html` : `chyps_response_${responseId}.pdf`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Fetch CSV export; auth required. Triggers browser download. */
export async function chypsDownloadExportCsv(): Promise<void> {
  const url = buildApiUrl('api/chyps/responses/export/');
  const res = await fetch(url, { headers: getHeaders(true) });
  if (!res.ok) throw new Error('Failed to export CSV');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'chyps_responses.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

// ---------- Dashboard (modality stats + cross-modality correlation) ----------

export interface ModalityStat {
  key: string;
  label: string;
  count: number;
}

/** Dashboard: patient counts (N) and measurement/test counts (from data folder). Auth required. */
export function getDashboardModalityStats(): Promise<{
  success: boolean;
  data: {
    patient_counts: ModalityStat[];
    measurement_counts: ModalityStat[];
  };
}> {
  return callApi('api/dashboard/modality-stats/', 'GET', undefined, true);
}

/** Dashboard: Visual Motion Sensitivity vs cervical proprioception correlation plot as PNG blob. Auth required. Optional cacheBuster (e.g. timestamp) to force refetch. */
export async function getDashboardCorrelationNeckcareChypsBlob(cacheBuster?: string): Promise<Blob> {
  let url = buildApiUrl('api/dashboard/correlation/neckcare-chyps/');
  if (cacheBuster != null) url += (url.includes('?') ? '&' : '?') + 'v=' + encodeURIComponent(cacheBuster);
  const res = await fetch(url, { method: 'GET', headers: getHeaders(true) });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to load correlation plot');
  }
  return res.blob();
}

/** Dashboard: Visual Motion Sensitivity vs MARCoNS (nasal bacterial culture test) test positivity correlation plot as PNG blob. Auth required. Optional cacheBuster to force refetch. */
export async function getDashboardCorrelationChypsMarconsBlob(cacheBuster?: string): Promise<Blob> {
  let url = buildApiUrl('api/dashboard/correlation/chyps-marcons/');
  if (cacheBuster != null) url += (url.includes('?') ? '&' : '?') + 'v=' + encodeURIComponent(cacheBuster);
  const res = await fetch(url, { method: 'GET', headers: getHeaders(true) });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to load Visual Motion Sensitivity–MARCoNS (nasal bacterial culture test) correlation plot');
  }
  return res.blob();
}

/** Dashboard: VNG Latency vs Cervical Proprioception Control (NeckCare Butterfly) correlation plot as PNG blob. Auth required. Optional cacheBuster to force refetch. */
export async function getDashboardCorrelationVngLatencyButterflyBlob(cacheBuster?: string): Promise<Blob> {
  let url = buildApiUrl('api/dashboard/correlation/vng-latency-butterfly/');
  if (cacheBuster != null) url += (url.includes('?') ? '&' : '?') + 'v=' + encodeURIComponent(cacheBuster);
  const res = await fetch(url, { method: 'GET', headers: getHeaders(true) });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to load VNG Latency vs Cervical Proprioception Control plot');
  }
  return res.blob();
}

/** Dashboard: regenerate correlation plot PNGs (saves to data/dashboard/). which: 'all' | 'neckcare-chyps' | 'chyps-marcons' | 'vng-latency-butterfly'. Auth required. */
export function getDashboardCorrelationRegenerate(which: 'all' | 'neckcare-chyps' | 'chyps-marcons' | 'vng-latency-butterfly' = 'all'): Promise<{
  success: boolean;
  generated?: string[];
  message?: string;
}> {
  return callApi('api/dashboard/correlation/regenerate/', 'POST', { which }, true);
}

/** Dashboard: clinical insight (clinician notes) per card. cardId: neckcare-chyps | chyps-marcons | vng-latency-butterfly. Auth required. */
export function getDashboardInsight(cardId: string): Promise<{ success: boolean; data?: { body: string; updated_at: string | null } }> {
  return callApi(`api/dashboard/insights/${encodeURIComponent(cardId)}/`, 'GET', undefined, true);
}

/** Dashboard: save clinical insight for a card. Auth required. */
export function putDashboardInsight(cardId: string, body: string): Promise<{ success: boolean; data?: { body: string; updated_at: string | null } }> {
  return callApi(`api/dashboard/insights/${encodeURIComponent(cardId)}/`, 'PUT', { body }, true);
}

/** Biomarker Center: patient with max fusion PDF modalities + per-modality metrics (RDS). Auth required. */
export type BiomarkerModalityRow = {
  fusion_key: string;
  label: string;
  resource_type: string;
  resource_id: string;
  metrics: Array<{ label: string; value: string; unit: string }>;
  s3_key: string | null;
};

export function getBiomarkerCenterProfile(): Promise<{
  success: boolean;
  data: {
    patient_id: string | null;
    distinct_modality_count: number;
    max_fusion_modalities?: number;
    gender: 'female' | 'male' | 'unknown';
    age: string | number | null;
    modalities: BiomarkerModalityRow[];
  };
}> {
  return callApi('api/dashboard/biomarker-center/profile/', 'GET', undefined, true);
}

/** Presigned GET URL for mirrored PDF (S3). Auth required. Prefer fetchBiomarkerPdfBlobUrl for in-app preview (same-origin, no S3 CORS). */
export function getBiomarkerPdfPresignUrl(resourceId: string): Promise<{
  success: boolean;
  message?: string;
  data?: { url: string; expires_in: number };
}> {
  const q = `resource_id=${encodeURIComponent(resourceId)}`;
  return callApi(`api/dashboard/biomarker-center/pdf/?${q}`, 'GET', undefined, true);
}

/** Load PDF via Django (streams from S3 server-side). Same-origin fetch + blob URL: works without S3 CORS; generic filename in Content-Disposition. */
export async function fetchBiomarkerPdfBlobUrl(resourceId: string): Promise<string> {
  const url = buildApiUrl(`api/dashboard/biomarker-center/pdf/file/?resource_id=${encodeURIComponent(resourceId)}`);
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) headers[AUTHORIZATION_HEADER_NAME as string] = `Token ${token}`;
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) throw new Error('Could not load PDF');
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Super-admin research portal: project summary (list). */
export type ResearchProjectSummary = {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
};

/** Super-admin research portal: full project with JSON config (stored in RDS). */
export type ResearchProject = ResearchProjectSummary & {
  config: Record<string, unknown>;
  created_by: number | null;
};

/** List research projects. Auth + allowed admin only. */
export function listResearchProjects(): Promise<{ success: boolean; data: { projects: ResearchProjectSummary[] } }> {
  return callApi('api/research/projects/', 'GET', undefined, true);
}

/** Create project from default template (budget, etc.). Optional title / config override. */
export function createResearchProject(payload?: {
  title?: string;
  config?: Record<string, unknown>;
}): Promise<{ success: boolean; data: { project: ResearchProject } }> {
  return callApi('api/research/projects/', 'POST', payload || {}, true);
}

export function getResearchProject(id: string): Promise<{ success: boolean; data: { project: ResearchProject } }> {
  return callApi(`api/research/projects/${encodeURIComponent(id)}/`, 'GET', undefined, true);
}

/** Partial update (title and/or config). */
export function patchResearchProject(
  id: string,
  payload: { title?: string; config?: Record<string, unknown> }
): Promise<{ success: boolean; data: { project: ResearchProject } }> {
  return callApi(`api/research/projects/${encodeURIComponent(id)}/`, 'PATCH', payload, true);
}

/** Full replace title + config. */
export function putResearchProject(
  id: string,
  payload: { title: string; config: Record<string, unknown> }
): Promise<{ success: boolean; data: { project: ResearchProject } }> {
  return callApi(`api/research/projects/${encodeURIComponent(id)}/`, 'PUT', payload, true);
}

export function deleteResearchProject(id: string): Promise<{ success: boolean; message?: string }> {
  return callApi(`api/research/projects/${encodeURIComponent(id)}/`, 'DELETE', undefined, true);
}

// ── Data Integration (portal explore) ────────────────────────────────────────

export interface PortalExploreResult {
  job_id: string;
  source: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_pages: number;
  patients_per_page: number;
  estimated_patients: number;
  meta?: { patients?: Array<{ id: number; name: string }> } | null;
  message: string;
  error: string;
  created_at: string;
  updated_at?: string;
}

export function launchChrome(url: string): Promise<{ ok: boolean; already_running: boolean; message: string; error?: string }> {
  return callApi('api/data-integration/launch-chrome/', 'POST', { url }, true);
}

export function checkCdp(): Promise<{ ok: boolean; already_running: boolean; message: string }> {
  return callApi('api/data-integration/launch-chrome/', 'POST', { url: 'about:blank' }, true);
}

export function explorePortal(
  source: string,
  extra?: Record<string, unknown>,
): Promise<{ success: boolean; job_id?: string; error?: string }> {
  return callApi('api/data-integration/explore/', 'POST', { source, ...extra }, true);
}

export function getLatestExploreResult(source: string): Promise<{ success: boolean } & PortalExploreResult> {
  return callApi(`api/data-integration/explore/?source=${encodeURIComponent(source)}`, 'GET', undefined, true)
    .catch((err: any) => {
      if (err?.response?.status === 404) return { success: false };
      throw err;
    });
}

export function getExploreStatus(jobId: string): Promise<{ success: boolean } & PortalExploreResult> {
  return callApi(`api/data-integration/explore/status/${jobId}/`, 'GET', undefined, true);
}

// ── Data Integration (crawler jobs) ──────────────────────────────────────────

export type CrawlerJobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface CrawlerJob {
  job_id: string;
  source: string;
  status: CrawlerJobStatus;
  percent: number;
  current_page: number;
  total_pages: number;
  downloaded_files: number;
  guided_mode?: boolean;
  selected_pages?: number[] | null;
  message: string;
  error: string;
  detail_log?: string[];
  created_at: string;
  updated_at: string;
}

export function startCrawlerJob(
  source: string,
  options?: { guided_mode?: boolean; pages?: number[] | 'all'; dry_run?: boolean; portal_prefix?: string },
): Promise<{ success: boolean; job_id?: string; error?: string }> {
  const body: Record<string, unknown> = {
    source,
    guided_mode: options?.guided_mode !== undefined ? options.guided_mode : true,
  };
  if (options?.pages !== undefined) body.pages = options.pages;
  if (options?.dry_run) body.dry_run = true;
  if (options?.portal_prefix) body.portal_prefix = options.portal_prefix;
  return callApi('api/data-integration/start/', 'POST', body, true);
}

export function getCrawlerJobStatus(jobId: string): Promise<{ success: boolean } & CrawlerJob> {
  return callApi(`api/data-integration/status/${jobId}/`, 'GET', undefined, true);
}

export function getCrawlerJobs(source?: string): Promise<{ success: boolean; jobs: CrawlerJob[] }> {
  const qs = source ? `?source=${encodeURIComponent(source)}` : '';
  return callApi(`api/data-integration/jobs/${qs}`, 'GET', undefined, true);
}

// ── Group Settings ────────────────────────────────────────────────────────────

export interface GroupSettings {
  group_id: number;
  group_name: string;
  jane_app_prefix: string;
}

export function getGroupSettings(): Promise<{ success: boolean; data?: GroupSettings; message?: string }> {
  return callApi('user/group-settings', 'GET', undefined, true)
    .catch((err: any) => {
      if (err?.response?.status === 404) return { success: false };
      throw err;
    });
}

export function updateGroupSettings(
  settings: Partial<Pick<GroupSettings, 'jane_app_prefix'>>,
): Promise<{ success: boolean; data?: Partial<GroupSettings>; message?: string }> {
  return callApi('user/group-settings', 'PATCH', settings, true);
}

// ── Portal Skills (MCP Agent Center) ─────────────────────────────────────────

export interface PortalSkill {
  id: number;
  name: string;
  display_name: string;
  portal_url: string;
  s3_bucket: string;
  s3_key: string;
  version: number;
  status: 'draft' | 'confirmed' | 'active' | 'deprecated';
  confirmed_by: number | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  content?: string | null;
  content_error?: string;
}

export function listPortalSkills(): Promise<{ skills: PortalSkill[] }> {
  return callApi('api/mcp/skills/', 'GET', undefined, true);
}

export function getPortalSkill(name: string): Promise<PortalSkill> {
  return callApi(`api/mcp/skills/${name}/`, 'GET', undefined, true);
}

export function createPortalSkill(data: {
  name: string;
  display_name: string;
  portal_url: string;
  s3_key: string;
  content: string;
  s3_bucket?: string;
  status?: string;
}): Promise<{ success: boolean; skill: PortalSkill }> {
  return callApi('api/mcp/skills/', 'POST', data, true);
}

export function updatePortalSkillStatus(
  name: string,
  newStatus: 'draft' | 'confirmed' | 'active' | 'deprecated',
): Promise<{ success: boolean; skill: PortalSkill }> {
  return callApi(`api/mcp/skills/${name}/`, 'PATCH', { status: newStatus }, true);
}

export interface SkillFeedbackResult {
  analysis: string;
  corrected_interpretation: Record<string, string>;
  selector_changes: Array<{ old: string; new: string; reason: string }>;
  code_diff: string;
  updated_markdown: string;
}

export function submitSkillFeedback(
  name: string,
  data: { description: string; html_snippet?: string; explore_stats?: Record<string, unknown>; context?: 'explore' | 'download' },
): Promise<{ success: boolean; result: SkillFeedbackResult }> {
  return callApi(`api/mcp/skills/${name}/feedback/`, 'POST', data, true);
}

// ── PDF Processing jobs ───────────────────────────────────────────────────────

export interface PdfProcessingJob {
  job_id: string;
  source: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  percent: number;
  total_pdfs: number;
  processed_pdfs: number;
  failed_pdfs: number;
  output_path: string;
  message: string;
  error: string;
  created_at: string;
  updated_at?: string;
}

export function startPdfProcessingJob(source: string): Promise<{ success: boolean; job_id?: string; error?: string }> {
  return callApi('api/data-integration/process-pdfs/', 'POST', { source }, true);
}

export function getPdfProcessingJobStatus(jobId: string): Promise<{ success: boolean } & PdfProcessingJob> {
  return callApi(`api/data-integration/process-pdfs/status/${jobId}/`, 'GET', undefined, true);
}

export function getPdfProcessingJobs(): Promise<{ success: boolean; jobs: PdfProcessingJob[] }> {
  return callApi('api/data-integration/process-pdfs/jobs/', 'GET', undefined, true);
}

// ── Patient Records ───────────────────────────────────────────────────────────

export interface PatientProfile {
  patient_uuid: string;
  source_vendor: string;
  display_id: string | null;
  chart_num: string | null;
  sex: string | null;
  age: number | null;
  new_patient_date: string | null;
  attachment_count: number;
  ehr_form_count: number;
  encrypted_pii: string | null;
  created_at: string;
}

export interface PatientEHRForm {
  id: string;
  file_name: string;
  form_date: string | null;
  template_name: string | null;
  macpractice_form_id: number | null;
  created_at: string;
}

export interface PatientAttachment {
  id: string;
  file_name: string;
  file_size: number;
  s3_key: string;
  s3_bucket: string;
  attached_date: string | null;
  description: string | null;
  is_pdf_parsed_for_patient: boolean;
  is_pdf_full_parsed: boolean;
  created_at: string;
}

export function setPiiSecret(secretHex: string): Promise<{ ok?: boolean; error?: string }> {
  return callApi('api/patients/set-secret/', 'POST', { secret_hex: secretHex }, true);
}

export function loadPiiSecret(): Promise<{ secret_hex?: string; error?: string }> {
  return callApi('api/patients/load-secret/', 'POST', {}, true);
}

export function startPatientUpload(
  source: string = 'macpractice',
  maxPatients?: number,
): Promise<{ success: boolean; job_id?: string; error?: string }> {
  const body: Record<string, unknown> = { source };
  if (maxPatients != null) body.max_patients = maxPatients;
  return callApi('api/patients/upload/start/', 'POST', body, true);
}

export function getPatients(
  page: number = 1,
  pageSize: number = 50,
  sourceVendor?: string,
  uuidSearch?: string,
): Promise<{ total: number; page: number; page_size: number; total_pages: number; patients: PatientProfile[] }> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (sourceVendor) params.set('source_vendor', sourceVendor);
  if (uuidSearch) params.set('uuid_search', uuidSearch);
  return callApi(`api/patients/?${params}`, 'GET', undefined, true);
}

export function getPatientAttachments(
  patientUuid: string,
  sourceVendor?: string,
): Promise<{ patient_uuid: string; attachments: PatientAttachment[] }> {
  const qs = sourceVendor ? `?source_vendor=${encodeURIComponent(sourceVendor)}` : '';
  return callApi(`api/patients/${encodeURIComponent(patientUuid)}/attachments/${qs}`, 'GET', undefined, true);
}

export function getAttachmentPresignedUrl(
  attachmentId: string,
  mode: 'view' | 'download' = 'view',
): Promise<{ url: string; file_name: string }> {
  return callApi(`api/patients/attachments/${encodeURIComponent(attachmentId)}/url/?mode=${mode}`, 'GET', undefined, true);
}

export function getPatientEHRForms(
  patientUuid: string,
): Promise<{ patient_uuid: string; ehr_forms: PatientEHRForm[] }> {
  return callApi(`api/patients/${encodeURIComponent(patientUuid)}/ehr-forms/`, 'GET', undefined, true);
}

export function getEHRFormContent(
  formId: string,
): Promise<{ html_content: string; file_name: string }> {
  return callApi(`api/patients/ehr-forms/${encodeURIComponent(formId)}/content/`, 'GET', undefined, true);
}

export async function downloadPatientEHRZip(patientUuid: string): Promise<void> {
  const url = buildApiUrl(`api/patients/${encodeURIComponent(patientUuid)}/ehr-forms/download-zip/`);
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) headers[AUTHORIZATION_HEADER_NAME as string] = `Token ${token}`;
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err: any = new Error(body.error || 'Failed to download EHR ZIP');
    err.data = body;
    throw err;
  }
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `patient_${patientUuid.slice(0, 8)}_ehr.zip`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function startJaneExport(
  patientUuids: string[],
  secretHex: string,
  exportAll = false,
): Promise<{ job_id: string }> {
  const url = buildApiUrl('api/patients/jane-export/');
  const token = getAuthToken();
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers[AUTHORIZATION_HEADER_NAME as string] = `Token ${token}`;
  const body = exportAll
    ? { export_all: true, pii_key_hex: secretHex }
    : { patient_uuids: patientUuids, pii_key_hex: secretHex };
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Jane export failed to start');
  }
  return res.json();
}

export async function getJaneExportStatus(
  jobId: string,
): Promise<{ status: string; processed: number; total: number; url?: string; error?: string }> {
  const url = buildApiUrl(`api/patients/jane-export/${encodeURIComponent(jobId)}/`);
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) headers[AUTHORIZATION_HEADER_NAME as string] = `Token ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Failed to get export status');
  return res.json();
}

export async function downloadPatientZip(patientUuid: string, sourceVendor?: string): Promise<void> {
  const qs = sourceVendor ? `?source_vendor=${encodeURIComponent(sourceVendor)}` : '';
  const url = buildApiUrl(`api/patients/${encodeURIComponent(patientUuid)}/attachments/download-zip/${qs}`);
  const token = getAuthToken();
  const headers: HeadersInit = {};
  if (token) headers[AUTHORIZATION_HEADER_NAME as string] = `Token ${token}`;
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) throw new Error('Failed to download ZIP');
  const disposition = res.headers.get('Content-Disposition');
  const suggested = disposition?.match(/filename="?([^";]+)"?/)?.[1];
  const filename = suggested || `patient_${patientUuid.slice(0, 8)}_records.zip`;
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── MCP Local Agent ───────────────────────────────────────────────────────────

export interface McpAgentStatus {
  online: boolean;
  token: string | null;
  platform: string;
  agent_version: string;
  last_seen: string | null;
}

export function getMcpStatus(): Promise<McpAgentStatus> {
  return callApi('api/mcp/status/', 'GET', undefined, true);
}

export function regenerateMcpToken(): Promise<{ token: string }> {
  return callApi('api/mcp/pair/', 'POST', {}, true);
}

export function revokeMcpToken(): Promise<{ ok: boolean }> {
  return callApi('api/mcp/pair/', 'DELETE', undefined, true);
}

export function getMcpAgentDownloadUrl(): Promise<{ url: string }> {
  return callApi('api/mcp/download/', 'GET', undefined, true);
}

export function confirmMcpLogin(): Promise<{ ok: boolean; error?: string }> {
  return callApi('api/mcp/confirm-login/', 'POST', {}, true);
}

// ── Skill Learn Requests ──────────────────────────────────────────────────────

export type LearnRequestStatus =
  | 'pending' | 'exploring' | 'attempting' | 'drafting'
  | 'verifying' | 'done' | 'failed' | 'cancelled';

export interface LearnLogEntry {
  ts: string;
  phase: string;
  message: string;
}

export interface SkillLearnRequest {
  id: number;
  portal_name: string;
  portal_type: string;
  portal_url: string;
  goal: string;
  status: LearnRequestStatus;
  agent_log: LearnLogEntry[];
  error: string;
  result_skill_name: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export function listLearnRequests(): Promise<{ requests: SkillLearnRequest[] }> {
  return callApi('api/mcp/learn-requests/', 'GET', undefined, true);
}

export function createLearnRequest(data: {
  portal_name: string;
  portal_url: string;
  goal: string;
  portal_type?: string;
}): Promise<{ request: SkillLearnRequest }> {
  return callApi('api/mcp/learn-requests/', 'POST', data, true);
}

export function getLearnRequest(id: number): Promise<SkillLearnRequest> {
  return callApi(`api/mcp/learn-requests/${id}/`, 'GET', undefined, true);
}

export function verifyPaymentSession(sessionId: string): Promise<any> {
  return callApi(`api/payments/verify-session?session_id=${encodeURIComponent(sessionId)}`, 'GET');
}

export function validateNpi(npiNumber: string): Promise<any> {
  return callApi('user/validate-npi', 'POST', { npi_number: npiNumber }, false);
}

// ── NeuroIQ / SkyTrak Golf ────────────────────────────────────────────────────

export function listNeuroTrainingSessions(): Promise<{ success: boolean; sessions: any[] }> {
  return callApi('api/data-integration/neuro-training/', 'GET', undefined, true);
}

export function createNeuroTrainingSession(payload: {
  title?: string;
  exercises?: string;
  start_date: string;
  end_date: string;
  pre_session_ids: string[];
  post_session_ids: string[];
  patient_uuid?: string;
  hw_exercise_ids?: string[];
}): Promise<{ success: boolean; session: any }> {
  return callApi('api/data-integration/neuro-training/', 'POST', payload, true);
}

export function deleteNeuroTrainingSession(id: string): Promise<{ success: boolean }> {
  return callApi(`api/data-integration/neuro-training/${id}/`, 'DELETE', undefined, true);
}

export function setNeuroTrainingSessionExercises(
  sessionId: string,
  exerciseIds: string[],
): Promise<{ success: boolean; hw_exercises: { id: string; name: string; category: string }[] }> {
  return callApi(`api/data-integration/neuro-training/${sessionId}/exercises/`, 'PATCH', { exercise_ids: exerciseIds }, true);
}

export function getSkyTrakSessions(patientUuid?: string): Promise<{ success: boolean; sessions: any[] }> {
  const qs = patientUuid ? `?patient_uuid=${encodeURIComponent(patientUuid)}` : '';
  return callApi(`api/data-integration/skytrak/sessions/${qs}`, 'GET', undefined, true);
}

export function deleteSkyTrakSession(sessionId: string): Promise<{ success: boolean }> {
  return callApi(`api/data-integration/skytrak/sessions/${encodeURIComponent(sessionId)}/`, 'DELETE', undefined, true);
}

export function getPortalPatients(search?: string): Promise<{ patients: { patient_uuid: string; first_name: string; last_name: string; django_user_id?: number }[] }> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  return callApi(`api/data-integration/portal-patients/${qs}`, 'GET', undefined, true);
}

export function getSkyTrakShots(sessionId?: string): Promise<{ success: boolean; shots: any[] }> {
  const qs = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
  return callApi(`api/data-integration/skytrak/shots/${qs}`, 'GET', undefined, true);
}

export function uploadSkyTrakCSV(
  files: File[]
): Promise<{ success: boolean; shots_saved: number; warnings?: string[] | null }> {
  const url = buildApiUrl('api/data-integration/skytrak/upload-csv/');
  const token = getAuthToken();
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  return fetch(url, {
    method: 'POST',
    headers: token ? { [AUTHORIZATION_HEADER_NAME as string]: `Token ${token}` } : {},
    body: form,
  })
    .then((r) => r.json())
    .then((data) => {
      if (!data.success) throw Object.assign(new Error(data.error || 'Upload failed'), { data });
      return data;
    });
}

// ── Homework & Training Sessions ──────────────────────────────────────────────

export interface HomeworkExercise {
  id: string;
  name: string;
  category: string;
  description: string;
  instructions: string;
  frequency_dose: string;
  targets: string;
  difficulty_level: number;
  duration_seconds: number | null;
  tags: string[];
  has_video: boolean;
  video_generated_at: string | null;
  is_active: boolean;
  created_at: string;
  video_url?: string | null;
}

export interface HomeworkAssignment {
  id: string;
  patient_id: number;
  exercise: HomeworkExercise;
  assigned_by_id: number;
  assigned_at: string;
  notes: string;
  repetitions: string;
  is_active: boolean;
  deactivated_at: string | null;
}


export interface HomeworkPatient {
  django_user_id: number;
  first_name: string;
  last_name: string;
}

export function getHomeworkExercises(category?: string): Promise<HomeworkExercise[]> {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  return callApi(`api/homework/exercises/${qs}`, 'GET', undefined, true);
}

export function getHomeworkExerciseDetail(id: string): Promise<HomeworkExercise> {
  return callApi(`api/homework/exercises/${encodeURIComponent(id)}/`, 'GET', undefined, true);
}

export function getHomeworkPortalPatients(): Promise<{ patients: HomeworkPatient[] }> {
  return callApi('api/homework/portal-patients/', 'GET', undefined, true);
}

export function getPatientHomeworkAssignments(patientId: number): Promise<HomeworkAssignment[]> {
  return callApi(`api/homework/patients/${patientId}/assignments/`, 'GET', undefined, true);
}

export function createHomeworkAssignment(
  patientId: number,
  exerciseId: string,
  notes: string,
  repetitions: string,
): Promise<HomeworkAssignment> {
  return callApi(`api/homework/patients/${patientId}/assignments/`, 'POST', {
    exercise_id: exerciseId,
    notes,
    repetitions,
  }, true);
}

export function deactivateHomeworkAssignment(assignmentId: string): Promise<{ detail: string }> {
  return callApi(`api/homework/assignments/${encodeURIComponent(assignmentId)}/`, 'DELETE', undefined, true);
}

export function getMyHomework(): Promise<HomeworkAssignment[]> {
  return callApi('api/homework/my/', 'GET', undefined, true);
}


// ── Token usage ───────────────────────────────────────────────────────────────

export interface TokenBalance {
  included_usd: string;
  included_used_usd: string;
  topup_balance_usd: string;
  allowance_reset_date: string | null;
  percent_used: number;
}

export interface TokenUsageStatus {
  balance: TokenBalance;
  warning: boolean;
  exceeded: boolean;
}

export function getTokenUsageStatus(): Promise<TokenUsageStatus> {
  return callApi('user/usage/tokens/', 'GET', undefined, true);
}

export function initiateTokenTopup(packageAmount: '10' | '25' | '50'): Promise<{
  payment_intent_id: string;
  client_secret: string;
  amount_usd: number;
}> {
  return callApi('user/usage/tokens/topup/', 'POST', { package: packageAmount }, true);
}

export function getAdminTokenUsage(): Promise<{
  groups: Array<{
    group_id: number;
    group_name: string;
    balance: TokenBalance;
    usage_30d_usd: string;
    usage_30d_calls: number;
  }>;
  users: Array<{
    user_id: number;
    email: string;
    group_id: number | null;
    group_name: string;
    usage_30d_usd: string;
    usage_30d_calls: number;
  }>;
}> {
  return callApi('user/admin/token-usage/', 'GET', undefined, true);
}

export function getAdminTokenUsageDrilldown(params: {
  user_id?: number;
  group_id?: number;
  page?: number;
}): Promise<{
  balance: TokenBalance;
  total_cost_usd: string;
  total_records: number;
  page: number;
  page_size: number;
  by_source: Record<string, { cost_usd: string; calls: number }>;
  by_user: Array<{ user_id: number | null; email: string; cost_usd: string; calls: number }>;
  records: any[];
}> {
  const qs = new URLSearchParams();
  if (params.user_id) qs.set('user_id', String(params.user_id));
  if (params.group_id) qs.set('group_id', String(params.group_id));
  if (params.page) qs.set('page', String(params.page));
  return callApi(`user/admin/token-usage/drilldown/?${qs}`, 'GET', undefined, true);
}

export function adminGrantCredits(params: {
  user_id?: number;
  group_id?: number;
  amount_usd: string;
  note?: string;
}): Promise<{ success: boolean; credits_added_usd: string; new_topup_balance_usd: string }> {
  return callApi('user/admin/grant-credits/', 'POST', params, true);
}

export function adminSetMfaRequired(
  userId: number,
  mfaRequired: boolean
): Promise<{ success: boolean; message?: string }> {
  return callApi('user/admin/set-mfa-required', 'POST', { user_id: userId, mfa_required: mfaRequired }, true);
}

// ── Patient Portal ─────────────────────────────────────────────────────────────

export interface PortalPatient {
  patient_uuid: string;
  first_name: string;
  last_name: string;
  dob: string;
  is_active: boolean;
  is_mfa_required: boolean;
  mfa_enabled: boolean;
  created_at: string;
}

export function listPortalPatients(): Promise<{ patients: PortalPatient[] }> {
  return callApi('user/patients/', 'GET', undefined, true);
}

export function createPortalPatient(params: {
  first_name: string;
  last_name: string;
  dob: string;
  password: string;
}): Promise<{ patient: PortalPatient }> {
  return callApi('user/patients/', 'POST', params, true);
}

export function deletePortalPatient(patientUuid: string): Promise<{ success: boolean }> {
  return callApi(`user/patients/${encodeURIComponent(patientUuid)}/`, 'DELETE', undefined, true);
}

export interface TrainingSessionLog {
  id: string;
  exercise_id: string;
  name: string;
  category: string;
  video_url: string | null;
}

export interface TrainingSessionRecord {
  id: string;
  notes: string;
  created_at: string;
  exercise_count: number;
  logged_exercises: TrainingSessionLog[];
}

export function listTrainingSessions(): Promise<TrainingSessionRecord[]> {
  return callApi('api/training-sessions/', 'GET', undefined, true);
}

export function createTrainingSession(params: {
  notes?: string;
  homework_ids?: string[];
}): Promise<TrainingSessionRecord> {
  return callApi('api/training-sessions/', 'POST', params, true);
}

export function getTrainingSession(id: string): Promise<TrainingSessionRecord> {
  return callApi(`api/training-sessions/${id}/`, 'GET', undefined, true);
}

export function patientPortalResetPassword(patientUuid: string, newPassword: string): Promise<{ success: boolean }> {
  return callApi('user/patients/reset-password', 'POST', { patient_uuid: patientUuid, new_password: newPassword }, true);
}

export function patientPortalSetMfaRequired(patientUuid: string, mfaRequired: boolean): Promise<{ success: boolean }> {
  return callApi('user/patients/set-mfa-required', 'POST', { patient_uuid: patientUuid, mfa_required: mfaRequired }, true);
}

export function patientLogin(params: {
  patient_uuid: string;
  password: string;
  totp_code?: string;
}): Promise<{
  auth_token: string;
  is_patient_session: boolean;
  patient_uuid: string;
  first_name: string;
  last_name: string;
  mfa_required?: boolean;
  mfa_enabled?: boolean;
}> {
  return callApi('user/patient-login', 'POST', params, false);
}
